/**
 * A birthday-list join carried across sign-up / sign-in (PRD 16 D2) — the same shape as
 * `birthday-request/pending-br-action.ts`. The join page stashes the token before raising the
 * authwall; once a FULL session lands, either the page itself (still mounted) or
 * `use-post-auth-funnel` (the page was swapped for the verify screen, or the member moved on)
 * makes the join call. Joining is idempotent server-side (`already_member`), but only one of the
 * two runs: the page registers itself in `liveListJoinPages` while it can resume.
 */
export interface PendingListJoin {
  token: string;
  /** The list's kind, for the funnel's `detail` (best effort). */
  kind?: string | null;
}

const KEY = "bday_pending_list_join";

/** Older than this = the member plainly moved on; don't join a list they've forgotten. */
const TTL_MS = 30 * 60_000;

export function setPendingListJoin(join: PendingListJoin): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...join, setAt: Date.now() }));
  } catch {
    /* private-mode / quota — non-fatal, the member taps Join again after signing in */
  }
}

export function peekPendingListJoin(): PendingListJoin | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingListJoin & { setAt?: number };
    // Validate the shape — garbage under the key must not reach the resume hook.
    const valid = typeof parsed.token === "string" && /^[A-Za-z0-9_-]{16,64}$/.test(parsed.token);
    if (!valid || !parsed.setAt || Date.now() - parsed.setAt > TTL_MS) {
      clearPendingListJoin();
      return null;
    }
    return { token: parsed.token, kind: parsed.kind ?? null };
  } catch {
    // Garbage JSON, or storage itself is off-limits (Safari blocked site data throws on any
    // access) — the guarded clear must not throw a second time.
    clearPendingListJoin();
    return null;
  }
}

export function clearPendingListJoin(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Tokens whose join page is mounted and will resume its own stash (see the module note). */
export const liveListJoinPages = new Set<string>();

/**
 * A join that landed `pending` (email not yet confirmed, D3). Once the member verifies and the
 * join page sees them active, it opens the list page WITH the "You're in" banner — the same
 * moment, from the member's side, as joining.
 */
const JUST_JOINED = "bday_list_just_joined:";

export function markJustJoined(token: string): void {
  try {
    sessionStorage.setItem(JUST_JOINED + token, String(Date.now()));
  } catch {
    /* private mode — the list page just opens without the banner */
  }
}

export function takeJustJoined(token: string): boolean {
  try {
    const at = Number(sessionStorage.getItem(JUST_JOINED + token));
    sessionStorage.removeItem(JUST_JOINED + token);
    return !!at && Date.now() - at < 24 * 60 * 60_000;
  } catch {
    return false;
  }
}
